import { MutableRefObject, useCallback, useMemo, useState } from 'react';
import { FilterTokenizer } from './FilterTokenizer';
import { SuggestionEngine } from './SuggestionEngine';
import { Suggestion, TokenType } from './types';
import { FilterCriteria } from '../../constants';
import { InsertionResult, insertSuggestion } from './autocompleteUtils';

interface UseFilterAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  filterOptions: Record<string, FilterCriteria>;
  shapeData?: any[];
  textAreaRef: MutableRefObject<HTMLTextAreaElement | null>;
}

interface UseFilterAutocompleteReturn {
  // State
  isAutocompleteOpen: boolean;
  cursorPosition: number;
  focusedSuggestionIndex: number;

  // Derived data
  suggestions: Suggestion[];
  currentToken: any;
  expectedNext: TokenType;

  // Actions
  setIsAutocompleteOpen: (open: boolean) => void;
  setCursorPosition: (position: number) => void;
  setFocusedSuggestionIndex: (index: number) => void;
  handleSelectSuggestion: (suggestion: Suggestion) => void;
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  handleSelect: (e: any) => void;
}

export function useFilterAutocomplete({
  value,
  onChange,
  filterOptions,
  shapeData,
  textAreaRef,
}: UseFilterAutocompleteProps): UseFilterAutocompleteReturn {
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(0);

  // Create suggestion engine
  const suggestionEngine = useMemo(() => {
    return new SuggestionEngine(filterOptions, shapeData);
  }, [filterOptions, shapeData]);

  // Tokenize and get suggestions
  const { tokens, currentToken, expectedNext, suggestions } = useMemo(() => {
    const tokenInfo = FilterTokenizer.tokenize(value, cursorPosition);
    const suggestions = suggestionEngine.getSuggestions(
      tokenInfo,
      '',
      value,
      cursorPosition
    );

    return {
      ...tokenInfo,
      suggestions,
    };
  }, [value, cursorPosition, suggestionEngine]);

  // Handle suggestion selection
  const handleSelectSuggestion = useCallback(
    (suggestion: Suggestion) => {
      if (suggestion.isHint) {
        return;
      }

      const tokenInfo = FilterTokenizer.tokenize(value, cursorPosition);
      const result: InsertionResult = insertSuggestion(
        suggestion,
        value,
        cursorPosition,
        tokenInfo.currentToken,
        tokenInfo.cursorContext
      );

      onChange(result.newValue);

      if (!result.shouldKeepOpen) {
        setIsAutocompleteOpen(false);
      }

      // Focus and position cursor
      setTimeout(() => {
        if (textAreaRef.current) {
          textAreaRef.current.focus();
          textAreaRef.current.setSelectionRange(
            result.newCursorPosition,
            result.newCursorPosition
          );
          setCursorPosition(result.newCursorPosition);

          // If keeping open, check for new suggestions
          if (result.shouldKeepOpen) {
            setTimeout(() => {
              const updatedTokenInfo = FilterTokenizer.tokenize(
                result.newValue,
                result.newCursorPosition
              );
              const newSuggestions = suggestionEngine.getSuggestions(
                updatedTokenInfo,
                '',
                result.newValue,
                result.newCursorPosition
              );

              if (newSuggestions.length > 0) {
                setIsAutocompleteOpen(true);
                setFocusedSuggestionIndex(0);
              } else {
                setIsAutocompleteOpen(false);
              }
            }, 10);
          }
        }
      }, 0);
    },
    [value, cursorPosition, onChange, textAreaRef, suggestionEngine]
  );

  // Handle text change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
      setCursorPosition(e.target.selectionStart || 0);

      // Open suggestions when typing
      if (e.target.value.length > 0 && suggestions.length > 0) {
        setIsAutocompleteOpen(true);
      }
    },
    [onChange, suggestions.length]
  );

  // Handle cursor position change
  const handleSelect = useCallback((e: any) => {
    setCursorPosition(e.target.selectionStart || 0);
  }, []);

  return {
    // State
    isAutocompleteOpen,
    cursorPosition,
    focusedSuggestionIndex,

    // Derived data
    suggestions,
    currentToken,
    expectedNext,

    // Actions
    setIsAutocompleteOpen,
    setCursorPosition,
    setFocusedSuggestionIndex,
    handleSelectSuggestion,
    handleChange,
    handleSelect,
  };
}
